import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { FotoUploader } from '../components/ui/FotoUploader';
import { PageHeader } from '../components/ui/PageHeader';
import {
  RESULT_BADGE,
  RESULT_LABEL,
  kontrollgaengeApi,
  type ChecklistItem,
  type Kontrollgang,
  type KontrollgangResultStatus,
} from '../lib/kontrollgaenge-api';
import { requireAccess } from '../lib/guards';
import { formatGermanDateTime } from '../lib/date-format';

export const Route = createFileRoute('/kontrollgaenge/$id')({
  beforeLoad: () => requireAccess('/api/v1/kontrollgaenge'),
  component: KontrollgangDetailPage,
});

const RESULT_OPTIONS: KontrollgangResultStatus[] = ['ok', 'mangel', 'nicht_zutreffend'];

function KontrollgangDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  return <KontrollgangDetail id={id} />;
}

export function KontrollgangDetail({ id }: { id: string }): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [finishNote, setFinishNote] = useState('');

  const kgQ = useQuery({
    queryKey: ['kontrollgaenge', 'detail', id],
    queryFn: () => kontrollgaengeApi.getById(id),
  });

  const checklistenQ = useQuery({
    queryKey: ['kontrollgaenge', 'checklisten'],
    queryFn: () => kontrollgaengeApi.listChecklisten(),
  });

  const finish = useMutation({
    mutationFn: () => kontrollgaengeApi.finish(id, finishNote.trim() || undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kontrollgaenge'] });
      setFinishNote('');
    },
  });

  const kg = kgQ.data?.item;
  const checkliste = checklistenQ.data?.items.find((c) => c.id === kg?.checklisteId);
  const items = checkliste?.itemsJsonb ?? [];
  const allRequiredDone = items
    .filter((i) => !i.optional)
    .every((i) => Boolean(kg?.ergebnisseJsonb[i.id]));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Kontrollgang"
        description={
          kg
            ? `${formatGermanDateTime(kg.kontrolliertAm)} · ${
                kg.abgeschlossen ? 'Abgeschlossen' : 'Offen'
              }`
            : '…'
        }
        actions={
          <Button variant="ghost" onClick={() => void navigate({ to: '/kontrollgaenge' })}>
            ← Zurück
          </Button>
        }
      />

      {kgQ.error ? <ErrorBanner error={kgQ.error as Error} /> : null}
      {finish.error ? <ErrorBanner error={finish.error as Error} /> : null}

      {!kg ? (
        <div className="text-sm text-slate-500">Lädt…</div>
      ) : (
        <>
          <ul className="space-y-3">
            {items.map((item) => (
              <ItemCard key={item.id} kontrollgangId={kg.id} kg={kg} item={item} />
            ))}
          </ul>

          {!kg.abgeschlossen ? (
            <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-slate-700">
                  Abschluss-Notiz (optional)
                </span>
                <textarea
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  rows={3}
                  value={finishNote}
                  onChange={(e) => setFinishNote(e.target.value)}
                  placeholder="Zusammenfassung, übergreifende Mängel…"
                />
              </label>
              <Button
                onClick={() => finish.mutate()}
                disabled={!allRequiredDone || finish.isPending}
                title={allRequiredDone ? '' : 'Erst alle Pflicht-Items erfassen'}
              >
                {finish.isPending ? 'Schließe ab…' : 'Kontrollgang abschließen'}
              </Button>
            </div>
          ) : kg.notiz ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Abschluss-Notiz
              </div>
              <div className="mt-1 whitespace-pre-wrap">{kg.notiz}</div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export function ItemCard({
  kontrollgangId,
  kg,
  item,
}: {
  kontrollgangId: string;
  kg: Kontrollgang;
  item: ChecklistItem;
}): JSX.Element {
  const qc = useQueryClient();
  const result = kg.ergebnisseJsonb[item.id];
  const [notiz, setNotiz] = useState(result?.notiz ?? '');

  const record = useMutation({
    mutationFn: (status: KontrollgangResultStatus) =>
      kontrollgaengeApi.recordResult(kontrollgangId, {
        itemId: item.id,
        status,
        ...(notiz.trim().length > 0 ? { notiz: notiz.trim() } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kontrollgaenge', 'detail', kontrollgangId] });
    },
  });

  const fotos = (kg.fotos ?? []).filter((f) => f.itemId === item.id);
  const disabled = kg.abgeschlossen || record.isPending;

  return (
    <li className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-slate-900">{item.label}</div>
          {item.optional ? <div className="text-xs text-slate-500">Optional</div> : null}
        </div>
        {result ? (
          <span
            className={`shrink-0 rounded-md border px-2 py-1 text-xs font-medium ${RESULT_BADGE[result.status]}`}
          >
            {RESULT_LABEL[result.status]}
          </span>
        ) : null}
      </div>

      {!kg.abgeschlossen ? (
        <>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-700">Notiz (optional)</span>
            <textarea
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              rows={2}
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              placeholder="z. B. Foto-Verweis, Mangel-Beschreibung"
              disabled={disabled}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            {RESULT_OPTIONS.map((s) => (
              <Button
                key={s}
                variant={result?.status === s ? 'primary' : 'ghost'}
                onClick={() => record.mutate(s)}
                disabled={disabled}
              >
                {RESULT_LABEL[s]}
              </Button>
            ))}
          </div>
        </>
      ) : result?.notiz ? (
        <div className="text-sm text-slate-700 whitespace-pre-wrap">{result.notiz}</div>
      ) : null}

      {fotos.length > 0 ? (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {fotos.map((f) => (
            <a
              key={f.id}
              href={kontrollgaengeApi.fotoUrl(kontrollgangId, f.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="aspect-square overflow-hidden rounded border border-slate-200 bg-slate-50"
              title={f.dateiname}
            >
              <img
                src={kontrollgaengeApi.fotoUrl(kontrollgangId, f.id)}
                alt={f.dateiname}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </a>
          ))}
        </div>
      ) : null}

      {!kg.abgeschlossen ? (
        <FotoUploader
          multiple={false}
          buttonLabel="Foto zum Item"
          onUpload={async (file) => {
            await kontrollgaengeApi.uploadFoto(kontrollgangId, file, item.id);
            await qc.invalidateQueries({ queryKey: ['kontrollgaenge', 'detail', kontrollgangId] });
          }}
        />
      ) : null}
    </li>
  );
}
